import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type LegacyRow = Record<string, unknown>;

type LegacySellerResponse = {
  ok?: boolean;
  result?: LegacyRow[];
  error?: string;
};

type StoreRaw = Record<string, unknown>;

type SellerStoresResponse = {
  ok?: boolean;
  stores?: StoreRaw[];
  data?: StoreRaw[];
  result?: StoreRaw[];
  error?: string;
};

type SellerStore = {
  id: number | string | null;
  fantasyName: string;
  legacyName: string;
};

type StoreUniverse = {
  store: SellerStore;
  rows: LegacyRow[];
};

type StoreFilter = "all" | string;

type DailyPoint = {
  label: string;
  count: number;
};

const pageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const toolbarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  flexWrap: "wrap",
};

const toolbarLeftStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
};

const selectLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 700,
  color: "var(--text-secondary)",
};

const selectStyle: React.CSSProperties = {
  minWidth: "210px",
  height: "38px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text-primary)",
  padding: "0 12px",
  fontSize: "13px",
  fontWeight: 700,
  outline: "none",
};

const smallMetaStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const cardsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "12px",
};

const featureCardStyle: React.CSSProperties = {
  minHeight: "154px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "18px",
};

const featureTopStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
};

const featureIconStyle: React.CSSProperties = {
  width: "38px",
  height: "38px",
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  fontSize: "18px",
  flexShrink: 0,
};

const featureTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const featureTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  fontSize: "13px",
  lineHeight: 1.45,
  color: "var(--text-secondary)",
};

const featureValueStyle: React.CSSProperties = {
  marginTop: "14px",
  fontSize: "26px",
  fontWeight: 900,
  color: "var(--text-primary)",
  letterSpacing: "-0.04em",
};

const chartCardStyle: React.CSSProperties = {
  padding: "18px",
};

const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
  marginBottom: "10px",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "17px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const chartWrapStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "205px",
  display: "flex",
  alignItems: "end",
  gap: "8px",
  padding: "12px 4px 2px 4px",
  borderTop: "1px solid var(--border)",
};

const chartItemStyle: React.CSSProperties = {
  flex: 1,
  minWidth: "38px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "6px",
};

const chartBarTrackStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "48px",
  height: "135px",
  borderRadius: "10px",
  background: "var(--surface-alt)",
  border: "1px solid var(--border)",
  display: "flex",
  alignItems: "flex-end",
  overflow: "hidden",
};

const chartBarStyle = (height: number): React.CSSProperties => ({
  width: "100%",
  height: `${height}%`,
  minHeight: height > 0 ? "8px" : "0",
  borderRadius: "10px",
  background:
    "linear-gradient(180deg, var(--primary) 0%, rgba(198,165,106,0.55) 100%)",
});

const chartCountStyle: React.CSSProperties = {
  fontSize: "12px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const chartLabelStyle: React.CSSProperties = {
  fontSize: "11px",
  color: "var(--text-secondary)",
  whiteSpace: "nowrap",
};

const metricListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  marginTop: "14px",
};

const metricRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "14px",
  padding: "9px 0",
  borderBottom: "1px solid var(--border)",
};

const metricKeyStyle: React.CSSProperties = {
  color: "var(--text-secondary)",
  fontSize: "13px",
};

const metricValueStyle: React.CSSProperties = {
  color: "var(--text-primary)",
  fontSize: "14px",
  fontWeight: 800,
  textAlign: "right",
};

const modalBackdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.58)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  zIndex: 1000,
};

const modalCardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "520px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "color-mix(in srgb, var(--surface) 94%, transparent)",
  boxShadow: "0 28px 80px rgba(0,0,0,0.34)",
  padding: "22px",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "22px",
  fontWeight: 900,
  color: "var(--text-primary)",
};

const modalTextStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  lineHeight: 1.65,
  color: "var(--text-secondary)",
};

const modalActionsStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  marginTop: "22px",
  flexWrap: "wrap",
};

const emptyStateStyle: React.CSSProperties = {
  padding: "24px 18px",
  borderRadius: "16px",
  border: "1px dashed var(--border)",
  textAlign: "center",
  color: "var(--text-secondary)",
  fontSize: "13px",
};

const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const getDeliveryStatus = (row: LegacyRow): string => {
  return (
    safeString(row.status_ml) ||
    safeString(row.status) ||
    safeString(row.estado) ||
    "Sin estado"
  );
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const normalized = value.replace(/\./g, "").replace(",", ".");
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const normalizeDiscount = (value: unknown): number => {
  const numeric = toNumber(value);
  return numeric > 1 ? numeric / 100 : numeric;
};

const formatMoney = (value: number): string =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value || 0);

const pickStoresArray = (payload: SellerStoresResponse): StoreRaw[] => {
  if (Array.isArray(payload?.stores)) return payload.stores;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const normalizeStore = (store: StoreRaw): SellerStore => {
  const id =
    (store.id as number | string | null | undefined) ??
    (store.store_id as number | string | null | undefined) ??
    null;

  const fantasyName =
    safeString(store.nombre_fantasia) ||
    safeString(store.nombreFantasia) ||
    safeString(store.name) ||
    safeString(store.store_name);

  const legacyName =
    safeString(store.legacy_fantasy_name) ||
    safeString(store.legacyFantasyName) ||
    safeString(store.legacy_name) ||
    fantasyName;

  return { id, fantasyName, legacyName };
};

const uniqueRowsByTracking = (rows: LegacyRow[]): LegacyRow[] => {
  const seen = new Set<string>();

  return rows.filter((row, index) => {
    const key =
      safeString(row.tracking) ||
      safeString(row.numero_tracking) ||
      safeString(row["Número Tracking"]) ||
      safeString(row.id_venta_ml) ||
      `row-${index}`;

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const getSummary = (rows: LegacyRow[]) => {
  const totalEnvios = rows.length;

  const entregados = rows.filter((row) =>
    getDeliveryStatus(row).toLowerCase().includes("entregado")
  ).length;

  const pendientes = Math.max(totalEnvios - entregados, 0);

  const montoBruto = rows.reduce((acc, row) => {
    return acc + toNumber(row.precio_cliente);
  }, 0);

  const descuentoPromedio =
    rows.length > 0
      ? rows.reduce((acc, row) => acc + normalizeDiscount(row.descuento), 0) /
        rows.length
      : 0;

  const netoEstimado = montoBruto * (1 - descuentoPromedio);

  return {
    totalEnvios,
    entregados,
    pendientes,
    montoBruto,
    descuentoPromedio,
    netoEstimado,
  };
};

const getRowDate = (row: LegacyRow): Date | null => {
  const raw =
    safeString(row.fecha_venta) ||
    safeString(row.fecha_colecta) ||
    safeString(row.fecha_wynflex) ||
    safeString(row.fecha_estado) ||
    safeString(row.created_at) ||
    safeString(row["Fecha Venta"]) ||
    safeString(row["Fecha Colecta"]) ||
    safeString(row["Fecha Wyn Flex"]) ||
    safeString(row["Fecha estado"]);

  if (!raw) return null;

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const match = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);

  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]) - 1;
  const year = Number(match[3].length === 2 ? `20${match[3]}` : match[3]);

  const date = new Date(year, month, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const buildDailyPoints = (rows: LegacyRow[]): DailyPoint[] => {
  const map = new Map<string, number>();

  rows.forEach((row) => {
    const date = getRowDate(row);
    if (!date) return;

    const key = date.toISOString().slice(0, 10);
    map.set(key, (map.get(key) || 0) + 1);
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, count]) => {
      const [, month, day] = key.split("-");
      return {
        label: `${day}/${month}`,
        count,
      };
    });
};

export default function SellerHome() {
  const { token, userId, username, firstName, lastName } = useAuth();

  const [stores, setStores] = useState<SellerStore[]>([]);
  const [storeUniverses, setStoreUniverses] = useState<StoreUniverse[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<StoreFilter>("all");
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [showBillingModal, setShowBillingModal] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const selectedUniverse = useMemo(() => {
    if (selectedStoreId === "all") return null;

    return (
      storeUniverses.find((item) => String(item.store.id) === selectedStoreId) ||
      null
    );
  }, [selectedStoreId, storeUniverses]);

  const visibleRows = useMemo(() => {
    if (selectedUniverse) return selectedUniverse.rows;
    return uniqueRowsByTracking(storeUniverses.flatMap((item) => item.rows));
  }, [selectedUniverse, storeUniverses]);

  const summary = useMemo(() => getSummary(visibleRows), [visibleRows]);
  const dailyPoints = useMemo(() => buildDailyPoints(visibleRows), [visibleRows]);

  const maxDailyCount = useMemo(() => {
    return Math.max(...dailyPoints.map((point) => point.count), 1);
  }, [dailyPoints]);

  const selectedStoreLabel = useMemo(() => {
    if (selectedStoreId === "all") return "Todas las tiendas";
    return selectedUniverse?.store.fantasyName || "Tienda";
  }, [selectedStoreId, selectedUniverse]);

  const primaryLegacyName = useMemo(() => {
    if (selectedUniverse) return selectedUniverse.store.legacyName;
    return stores[0]?.legacyName || "";
  }, [selectedUniverse, stores]);

  const fetchSellerLegacyUniverse = useCallback(async () => {
    if (!token || !userId || !username) {
      setError("No se pudo resolver la sesión del seller.");
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      setError("");

      const roleResponse = await authFetch(`/v2/users/${userId}/role`, {
        token,
      });

      const rolePayload = await roleResponse.json().catch(() => ({}));

      if (!roleResponse.ok || rolePayload?.ok === false) {
        throw new Error(
          rolePayload?.error || "No se pudo resolver el seller asociado al usuario."
        );
      }

      const sellerId = rolePayload?.data?.id;

      if (!sellerId) {
        throw new Error("El usuario no tiene seller_data asociado.");
      }

      const storesResponse = await authFetch(`/v2/sellers/${sellerId}/store`, {
        token,
      });

      const storesPayload = (await storesResponse
        .json()
        .catch(() => ({}))) as SellerStoresResponse;

      if (!storesResponse.ok || storesPayload?.ok === false) {
        throw new Error(
          storesPayload?.error ||
            "No se pudieron cargar las tiendas asociadas al seller."
        );
      }

      const parsedStores = pickStoresArray(storesPayload)
        .map(normalizeStore)
        .filter((store) => Boolean(store.fantasyName || store.legacyName));

      setStores(parsedStores);

      if (!parsedStores.length) {
        setStoreUniverses([]);
        setSelectedStoreId("all");
        setError(
          "Este seller no tiene tiendas asociadas. Asociá un store antes de consultar legacy."
        );
        return;
      }

      const universes: StoreUniverse[] = [];

      for (const store of parsedStores) {
        const legacyResponse = await authFetch("/v1/data/client/me", {
          method: "POST",
          token,
          json: true,
          body: JSON.stringify({
            username: store.legacyName,
          }),
        });

        const legacyPayload = (await legacyResponse
          .json()
          .catch(() => ({}))) as LegacySellerResponse;

        if (!legacyResponse.ok || legacyPayload?.ok === false) {
          throw new Error(
            legacyPayload?.error ||
              `No se pudo cargar la data legacy para ${store.legacyName}.`
          );
        }

        universes.push({
          store,
          rows: uniqueRowsByTracking(
            Array.isArray(legacyPayload.result) ? legacyPayload.result : []
          ),
        });
      }

      setStoreUniverses(universes);

      if (
        selectedStoreId !== "all" &&
        !universes.some((item) => String(item.store.id) === selectedStoreId)
      ) {
        setSelectedStoreId("all");
      }
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "Error cargando seller.");
      setStores([]);
      setStoreUniverses([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, userId, username, selectedStoreId, firstName, lastName]);

  useEffect(() => {
    fetchSellerLegacyUniverse();
  }, [fetchSellerLegacyUniverse]);

  useEffect(() => {
    const openBillingModal = () => setShowBillingModal(true);

    window.addEventListener("seller:open-billing-modal", openBillingModal);

    return () => {
      window.removeEventListener("seller:open-billing-modal", openBillingModal);
    };
  }, []);

  const handleDownloadExcel = async () => {
    if (!token) return;

    if (!primaryLegacyName) {
      alert("No hay una tienda asociada para descargar.");
      return;
    }

    try {
      setDownloading(true);

      const response = await authFetch("/v1/data/client/print", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({
          username: primaryLegacyName,
        }),
      });

      if (!response.ok) {
        const maybeJson = await response.json().catch(() => null);
        throw new Error(maybeJson?.error || "No se pudo descargar el Excel.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeFileName = primaryLegacyName.replace(/\s+/g, "_").toLowerCase();

      link.href = url;
      link.download = `envios_${safeFileName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowBillingModal(false);
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "Error descargando Excel.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <section style={toolbarStyle}>
        <div>
          <div style={smallMetaStyle}>Vista actual</div>
          <div style={{ fontSize: "17px", fontWeight: 800, color: "var(--text-primary)" }}>
            {selectedStoreLabel}
          </div>
        </div>

        <div style={toolbarLeftStyle}>
          <span style={selectLabelStyle}>Ver por tienda</span>

          <select
            style={selectStyle}
            value={selectedStoreId}
            onChange={(event) => setSelectedStoreId(event.target.value)}
            disabled={loading || !stores.length}
          >
            <option value="all">Todas las tiendas</option>
            {stores.map((store) => (
              <option key={String(store.id)} value={String(store.id)}>
                {store.fantasyName}
              </option>
            ))}
          </select>

          <Button
            variant="secondary"
            onClick={() => {
              setRefreshing(true);
              fetchSellerLegacyUniverse();
            }}
            disabled={refreshing}
          >
            {refreshing ? "Actualizando..." : "Actualizar"}
          </Button>
        </div>
      </section>

      <section style={cardsGridStyle}>
        <Card style={featureCardStyle}>
          <div>
            <div style={featureTopStyle}>
              <h2 style={featureTitleStyle}>Facturación</h2>
              <div style={featureIconStyle}>◷</div>
            </div>
              <p style={featureTextStyle}>Descarga tu detalle semanal</p>

            <div style={featureValueStyle}>{formatMoney(summary.netoEstimado)}</div>
          </div>

          <Button onClick={() => setShowBillingModal(true)} disabled={!primaryLegacyName}>
            Ver resumen
          </Button>
        </Card>

        <Card style={featureCardStyle}>
          <div>
            <div style={featureTopStyle}>
              <h2 style={featureTitleStyle}>Envío rápido</h2>
              <div style={featureIconStyle}>⚡</div>
            </div>
<p style={featureTextStyle}>Hacer un envío</p>
            <div style={featureValueStyle}>Próximamente</div>
          </div>

          <Button variant="secondary" disabled>
            Solicitar un envío
          </Button>
        </Card>

        <Card style={featureCardStyle}>
          <div>
            <div style={featureTopStyle}>
              <div>
                <h2 style={featureTitleStyle}>Turbo</h2>
                <p style={featureTextStyle}>Activar servicio de envíos turbo</p>
              </div>

              <div style={featureIconStyle}>🚀</div>
            </div>

            <div style={featureValueStyle}>Próximamente</div>
          </div>

          <Button variant="secondary" disabled>
            Activar Turbo
          </Button>
        </Card>
      </section>

      <Card style={chartCardStyle}>
        <div style={sectionHeaderStyle}>
          <h2 style={sectionTitleStyle}>Envíos de la semana</h2>

          <div style={{ ...smallMetaStyle, textAlign: "right" }}>
            {selectedStoreLabel}
          </div>
        </div>

        {loading ? (
          <div style={emptyStateStyle}>Cargando envíos...</div>
        ) : error ? (
          <div style={emptyStateStyle}>{error}</div>
        ) : dailyPoints.length ? (
          <div style={chartWrapStyle}>
            {dailyPoints.map((point) => {
              const height = Math.max((point.count / maxDailyCount) * 100, 4);

              return (
                <div key={point.label} style={chartItemStyle}>
                  <div style={chartCountStyle}>{point.count}</div>
                  <div style={chartBarTrackStyle}>
                    <div style={chartBarStyle(height)} />
                  </div>
                  <div style={chartLabelStyle}>{point.label}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={emptyStateStyle}>
            No hay envíos con fecha reconocible para graficar.
          </div>
        )}
      </Card>

      {showBillingModal ? (
        <div style={modalBackdropStyle} onClick={() => setShowBillingModal(false)}>
          <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
            <h3 style={modalTitleStyle}>Facturación</h3>
            <p style={modalTextStyle}>
              Descargá el detalle disponible para la tienda seleccionada.
            </p>

            <div style={metricListStyle}>
              <div style={metricRowStyle}>
                <span style={metricKeyStyle}>Tienda</span>
                <span style={metricValueStyle}>{selectedStoreLabel}</span>
              </div>

              <div style={metricRowStyle}>
                <span style={metricKeyStyle}>Envíos visibles</span>
                <span style={metricValueStyle}>{summary.totalEnvios}</span>
              </div>

              <div style={metricRowStyle}>
                <span style={metricKeyStyle}>Entregados</span>
                <span style={metricValueStyle}>{summary.entregados}</span>
              </div>

              <div style={metricRowStyle}>
                <span style={metricKeyStyle}>Pendientes</span>
                <span style={metricValueStyle}>{summary.pendientes}</span>
              </div>

              <div style={metricRowStyle}>
                <span style={metricKeyStyle}>Bruto visible</span>
                <span style={metricValueStyle}>{formatMoney(summary.montoBruto)}</span>
              </div>

              <div style={metricRowStyle}>
                <span style={metricKeyStyle}>Neto estimado</span>
                <span style={metricValueStyle}>{formatMoney(summary.netoEstimado)}</span>
              </div>
            </div>

            <div style={modalActionsStyle}>
              <Button variant="ghost" onClick={() => setShowBillingModal(false)}>
                Cerrar
              </Button>

              <Button
                onClick={handleDownloadExcel}
                disabled={downloading || loading || !primaryLegacyName}
              >
                {downloading ? "Descargando..." : "Descargar Excel"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}